import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { theme } from '_constants/theme';
import { STORAGE_KEYS } from '_constants/storage';
import { MODALS_KEYS } from '_constants/modals';
import { FIELDS_KEYS, STATE_MAP } from '_constants/forms';
import { PRODUCT_CODES } from '_constants/products';
import { ROUTES, PARAMS } from '_constants/routes';
import { createQueryString } from '_helpers/urls';
import { getProcessingLabel } from '_helpers/processing';
import { getEntityType } from '_helpers/filingFlow';
import { clearSessionValue } from '_helpers/storage';

import { hideModal, showModal } from '_redux/modals/slice';
import {
  refreshForm,
  resetForm,
  refreshProducts,
  refreshPostSelectedProducts,
  refreshPostSelectedProductsEdit,
} from '_redux/order/slice';

import { selectActiveProductCode } from '_redux/products/selector';
import {
  selectFormValues as selectFilingFormValues,
  selectProductCodesFromPackage,
  selectPostSelectedProductCodesFromPackage,
} from '_redux/order/selector';
import { selectModalsVisibility } from '_redux/modals/selector';

import StateAndEntitySelectSection2 from '_components/composed/sections/StateAndEntitySelectSection2';
import PackageSelectSection from '_components/composed/sections/PackageSelectSection';
import PackageFAQSection from '_components/composed/sections/PackageFAQSection';
import ConfirmationModal from '_components/composed/modals/ConfirmationModal';
import Text from '_components/atomic/layouts/Card/Text';
import { getFormKeys } from '_components/composed/sections/FilingSection';
import { transformRowsToIncludedProducts } from '_components/composed/sections/PackageSelectSection/helpers';

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PackagePage = () => {
  const mountedRef = useRef(false);
  const stateRef = useRef(null);
  const router = useRouter();
  const dispatch = useDispatch();
  const activeProductCode = useSelector(selectActiveProductCode);
  const filingFormValues = useSelector(selectFilingFormValues);
  const modalsVisibility = useSelector(selectModalsVisibility);
  const productCodesFromPackage = useSelector(selectProductCodesFromPackage);
  const postSelectedProductCodesFromPackage = useSelector(
    selectPostSelectedProductCodesFromPackage
  );

  const formKeys = getFormKeys({
    productCode: activeProductCode,
    processingCode: filingFormValues[FIELDS_KEYS.processingOption],
    productCodesFromPackage,
    postSelectedProductCodesFromPackage,
  });

  const handleCancel = () => {
    dispatch(refreshProducts([PRODUCT_CODES.incLLC]));
    dispatch(refreshPostSelectedProducts([]));
    dispatch(refreshPostSelectedProductsEdit([]));
    dispatch(resetForm());
    clearSessionValue(STORAGE_KEYS.businessInfo);
    dispatch(hideModal(MODALS_KEYS.confirmation));

    if (stateRef.current) {
      dispatch(refreshForm({ [FIELDS_KEYS.organizedState]: stateRef.current }));
    }
  };

  const handleConfirm = () => {
    dispatch(
      refreshForm({ [FIELDS_KEYS.step]: filingFormValues[FIELDS_KEYS.maxStep] })
    );

    if (
      filingFormValues[FIELDS_KEYS.maxStep] === formKeys.length - 1 &&
      filingFormValues[FIELDS_KEYS.license]
    ) {
      router.push(
        `${ROUTES.incFilingReview}${createQueryString({
          [PARAMS.entityType]: getEntityType(activeProductCode),
          [PARAMS.entityState]: filingFormValues[FIELDS_KEYS.organizedState],
          [PARAMS.packageId]: getProcessingLabel(
            filingFormValues[FIELDS_KEYS.processingOption]
          ),
        })}`,
        undefined,
        { shallow: true }
      );
    } else {
      router.push(
        `${ROUTES.incFiling}${createQueryString({
          [PARAMS.entityType]: getEntityType(activeProductCode),
          [PARAMS.entityState]: filingFormValues[FIELDS_KEYS.organizedState],
          [PARAMS.packageId]: getProcessingLabel(
            filingFormValues[FIELDS_KEYS.processingOption]
          ),
          [PARAMS.step]: filingFormValues[FIELDS_KEYS.maxStep],
          [PARAMS.formId]: formKeys[filingFormValues[FIELDS_KEYS.maxStep]],
        })}`,
        undefined,
        { shallow: true }
      );
    }
    dispatch(hideModal(MODALS_KEYS.confirmation));
  };

  useEffect(() => {
    const { isReady, query } = router;
    const { entityType, ...restQuery } = query;

    if (isReady && activeProductCode) {
      const entityTypeValue = getEntityType(activeProductCode);

      if (entityType !== entityTypeValue) {
        router.replace(
          `${ROUTES.incPackage}${createQueryString({
            ...restQuery,
            [PARAMS.entityType]: entityTypeValue,
          })}`,
          undefined,
          { shallow: true }
        );
      }
    }
  }, [router, activeProductCode]);

  useEffect(() => {
    const { isReady, query } = router;
    const { entityState, ...restQuery } = query;

    if (isReady && filingFormValues.organizedState) {
      if (entityState !== filingFormValues.organizedState) {
        router.replace(
          `${ROUTES.incPackage}${createQueryString({
            ...restQuery,
            [PARAMS.entityState]: filingFormValues.organizedState,
          })}`,
          undefined,
          { shallow: true }
        );
      }
    } else {
      if (Object.keys(STATE_MAP).includes(entityState)) {
        dispatch(refreshForm({ [FIELDS_KEYS.organizedState]: entityState }));
      }
    }
  }, [dispatch, router, filingFormValues]);

  useEffect(() => {
    const { isReady, query } = router;
    const { packageId, ...restQuery } = query;

    if (isReady && filingFormValues.processingOption) {
      const processingLabel =
        getProcessingLabel(filingFormValues.processingOption) ||
        filingFormValues.processingOption;

      if (packageId !== processingLabel) {
        router.replace(
          `${ROUTES.incPackage}${createQueryString({
            ...restQuery,
            [PARAMS.packageId]: processingLabel,
          })}`,
          undefined,
          { shallow: true }
        );
      }
    }
  }, [router, filingFormValues]);

  useEffect(() => {
    if (mountedRef.current) {
      return;
    }

    if (filingFormValues[FIELDS_KEYS.maxStep] > 0) {
      dispatch(showModal(MODALS_KEYS.confirmation));
      stateRef.current = filingFormValues[FIELDS_KEYS.organizedState];
      mountedRef.current = true;
    }
  }, [dispatch, filingFormValues]);

  useEffect(() => {
    if (
      !productCodesFromPackage?.length &&
      activeProductCode &&
      filingFormValues.processingOption
    ) {
      dispatch(
        refreshProducts([
          activeProductCode,
          ...transformRowsToIncludedProducts({
            activeProcessingCode: filingFormValues.processingOption,
          }),
        ])
      );
    }
  }, [dispatch, activeProductCode, productCodesFromPackage, filingFormValues]);

  return (
    <StyledContainer>
      <StateAndEntitySelectSection2 />
      {activeProductCode && filingFormValues[FIELDS_KEYS.organizedState] && (
        <PackageSelectSection />
      )}
      <PackageFAQSection />
      <ConfirmationModal
        visible={modalsVisibility[MODALS_KEYS.confirmation]}
        cancelLabel="Start Over"
        confirmLabel="Resume Order"
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      >
        <Text size={theme.fontSizes.regular} weight={400}>
          It appears you may already have an order in progress.
        </Text>
      </ConfirmationModal>
    </StyledContainer>
  );
};

export default PackagePage;
